// src/components/GrowthStatsPreview.jsx
import React, { useState } from "react";
import styled from "styled-components";
import {
  FaChartLine,
  FaTasks,
  FaCheckCircle,
  FaFire,
  FaChevronUp,
  FaStar,
  FaBolt,
  FaMedal,
  FaTrophy,
} from "react-icons/fa";
import { Link } from "react-router-dom";

const PreviewContainer = styled.div`
  padding: 20px;
  background-color: var(--secondaryBackgroundColor);
  border-radius: 14px;
  height: 100%;
  min-height: 350px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
`;

const Header = styled.h2`
  text-align: center;
  font-weight: 500;
  margin: 0 0 20px 0;
  font-size: 18px;
  font-family: "Montserrat", sans-serif;
  color: var(--fontPrimaryColor);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-bottom: 20px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const StatCard = styled.div`
  background: var(--thirdBackgroundColor);
  border-radius: 12px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  opacity: 0.7;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 5px;
`;

const IconWrapper = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ color }) => `${color}15`};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ color }) => `${color}30`};

  svg {
    color: ${({ color }) => color};
    font-size: 1rem;
  }
`;

const StatInfo = styled.div`
  flex: 1;
`;

const StatTitle = styled.h3`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--fontPrimaryColor);
  margin: 0;
  margin-bottom: 2px;
`;

const StatPeriod = styled.span`
  font-size: 0.7rem;
  color: var(--fontSecondaryColor);
  opacity: 0.8;
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${({ color }) => color};
  margin: 5px 0;
`;

const StatSubtitle = styled.p`
  font-size: 0.75rem;
  color: var(--fontSecondaryColor);
  margin: 0;
  line-height: 1.4;
`;

const TrendBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 500;
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.3);
`;

const AdditionalInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 0.7rem;
  color: var(--fontSecondaryColor);

  svg {
    font-size: 0.8rem;
    color: ${({ color }) => color};
  }
`;

const BlurOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  backdrop-filter: ${(props) => (props.$active ? "blur(8px)" : "none")};
  background: ${(props) =>
    props.$active ? "rgba(0, 0, 0, 0.7)" : "transparent"};
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${(props) => (props.$active ? 10 : -1)};
  opacity: ${(props) => (props.$active ? 1 : 0)};
  transition: all 0.3s ease;
`;

const UpgradeMessage = styled.div`
  background: var(--primaryColor);
  color: white;
  padding: 20px 30px;
  border-radius: 10px;
  text-align: center;
  max-width: 80%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  z-index: 11;

  h3 {
    margin: 0 0 10px 0;
    font-size: 1.2rem;
  }

  p {
    margin: 0 0 15px 0;
    font-size: 0.95rem;
    opacity: 0.9;
  }

  button {
    background: white;
    color: var(--primaryColor);
    border: none;
    padding: 10px 20px;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }
  }
`;

const GrowthStatsPreview = () => {
  const [showUpgradeMessage, setShowUpgradeMessage] = useState(false);
  const [hovering, setHovering] = useState(false);

  const demoStats = [
    {
      title: "Growth Score",
      value: 142,
      color: "#3b82f6",
      icon: FaChartLine,
      period: "Last 7 days",
      subtitle: "Total completed activities",
      trend: 12,
      additional: "78% completion rate",
      additionalIcon: FaStar,
    },
    {
      title: "Tasks Completed",
      value: 89,
      color: "#10b981",
      icon: FaTasks,
      period: "Last 7 days",
      subtitle: "Finished across all projects",
      additional: "Consistent execution",
      additionalIcon: FaBolt,
    },
    {
      title: "Habits Completed",
      value: 53,
      color: "#f59e0b",
      icon: FaCheckCircle,
      period: "Last 7 days",
      subtitle: "Daily routines maintained",
      additional: "Building lasting routines",
      additionalIcon: FaMedal,
    },
    {
      title: "Peak Performance",
      value: 24,
      color: "#ef4444",
      icon: FaFire,
      period: "Record day",
      subtitle: "Most completions in a day",
      additional: "Record productivity",
      additionalIcon: FaTrophy,
    },
  ];

  const handleContainerClick = () => {
    setShowUpgradeMessage(true);
  };

  const handleUpgradeClick = () => {
    window.location.href = "/upgrade";
  };

  const handleCloseUpgrade = (e) => {
    e.stopPropagation();
    setShowUpgradeMessage(false);
    setHovering(false);
  };

  const handleMouseEnter = () => {
    setHovering(true);
  };

  const handleMouseLeave = () => {
    if (!showUpgradeMessage) {
      setHovering(false);
    }
  };

  return (
    <PreviewContainer
      onClick={handleContainerClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Header>Growth Stats</Header>

      <StatsGrid>
        {demoStats.map((stat, index) => (
          <StatCard key={index}>
            <StatHeader>
              <IconWrapper color={stat.color}>
                <stat.icon />
              </IconWrapper>
              <StatInfo>
                <StatTitle>{stat.title}</StatTitle>
                <StatPeriod>{stat.period}</StatPeriod>
              </StatInfo>
              {stat.trend && (
                <TrendBadge>
                  <FaChevronUp />
                  {stat.trend}%
                </TrendBadge>
              )}
            </StatHeader>
            <StatValue color={stat.color}>{stat.value}</StatValue>
            <StatSubtitle>{stat.subtitle}</StatSubtitle>
            <AdditionalInfo color={stat.color}>
              <stat.additionalIcon /> <span>{stat.additional}</span>
            </AdditionalInfo>
          </StatCard>
        ))}
      </StatsGrid>

      <BlurOverlay $active={showUpgradeMessage || hovering}>
        {showUpgradeMessage ? (
          <UpgradeMessage onClick={(e) => e.stopPropagation()}>
            <h3>🔒 Growth Analytics Pro</h3>
            <p>
              Track your productivity growth with detailed analytics,
              performance trends, and actionable insights to optimize your
              workflow.
            </p>
            <button onClick={handleUpgradeClick}>Upgrade to PRO</button>
            <button
              onClick={handleCloseUpgrade}
              style={{
                background: "transparent",
                color: "white",
                marginTop: "10px",
                fontSize: "0.85rem",
              }}
            >
              Maybe later
            </button>
          </UpgradeMessage>
        ) : hovering ? (
          <UpgradeMessage onClick={(e) => e.stopPropagation()}>
            <h3>Click to unlock Growth Analytics</h3>
            <p>
              This is a preview of Growth Analytics. Upgrade to PRO to track
              your actual productivity metrics and growth trends.
            </p>
          </UpgradeMessage>
        ) : null}
      </BlurOverlay>
    </PreviewContainer>
  );
};

export default GrowthStatsPreview;
